import { GridPagerSettings } from '@progress/kendo-react-grid';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import CardGridWrapper from 'src/components/CardGridWrapper';
import { ShoppingListProduct } from 'src/types/shopping_lists/ShoppingList';
import { useContextSelector } from 'use-context-selector';
import type { GridReadyShoppingListProduct } from './shoppingListProductMapper';
import ShoppingListCardGridHeader from './ShoppingListCardGridHeader';
import ShoppingListPictureViewCard from './ShoppingListPictureViewCard';
import ShoppingListProductInformationPanel from './ShoppingListProductInformationPanel';
import { ShoppingListProductsContext } from './ShoppingListProductsContext';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const ShoppingListProductPictureView: FC = () => {
  const { t } = useTranslation();

  const listId = useContextSelector(ShoppingListProductsContext, (state) => state.listId);
  const gridDataState = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.gridDataState
  );
  const onDataStateChange = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.onDataStateChange
  );
  const loadingProducts = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.loadingProducts
  );
  const getProductSelection = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.getProductSelection
  );
  const updateProductSelection = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.updateProductSelection
  );
  const sourceProducts = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.sourceProducts
  );
  const filterSearch = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.filterSearch
  );
  const isProductPanelOpen = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    ShoppingListProductsContext,
    (state) => state.handleCloseProductPanel
  );

  const hasFilterSearchValue = Boolean(filterSearch.trim());

  const renderCard = (item: GridReadyShoppingListProduct) => {
    const { selected, qty } = getProductSelection(item.productKey);

    return (
      <ShoppingListPictureViewCard
        key={item.productKey}
        product={item as ShoppingListProduct}
        selected={selected}
        onSelect={() => {
          updateProductSelection(item.productKey, {
            selected: !selected,
            qty: !selected && qty === 0 ? 1 : !selected ? qty : 0,
          });
        }}
        onQuantityChange={(newQty: number) => {
          updateProductSelection(item.productKey, {
            qty: newQty,
            selected: newQty !== 0,
          });
        }}
        quantity={qty}
      />
    );
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {hasFilterSearchValue && (
        <div className="mb-2 shrink-0 truncate px-4 text-sm font-[600]">
          {t('Showing results for')} &quot;{filterSearch.trim()}&quot;
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        <CardGridWrapper<GridReadyShoppingListProduct>
          key={`shopping-list-products-${listId}-${gridDataState.skip}-${gridDataState.take}`}
          dataSource={sourceProducts ?? []}
          renderItem={renderCard}
          dataState={gridDataState}
          onDataStateChange={onDataStateChange}
          pageable={gridPagerSettings}
          loading={loadingProducts}
          className="min-h-0 flex-1"
          tableTextId="Items"
          stickyPager
          gap="gap-x-3 gap-y-2.5"
          height="100%"
          headerChildren={<ShoppingListCardGridHeader />}
        />
      </div>
      <ShoppingListProductInformationPanel
        isOpen={isProductPanelOpen}
        onClose={handleCloseProductPanel}
      />
    </div>
  );
};

export default ShoppingListProductPictureView;
